import React, { useEffect, useMemo, useState } from "react";

import { ChronoUnit, convert } from "@js-joda/core";
import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import {
  AnalyticsResultDto,
  AnyFilterExpressionInput,
  MdmObjectDto,
  SortDirection,
  SortInput,
  useGetAnalyticsByObjectIdPagedLazyQuery,
  useGetAnalyticsByProjectIdPagedLazyQuery,
} from "gql/generated/types";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { getFirstDayOfHalfYear, getLastDayOfCurrentMonthDate } from "utils/joda-time-utils";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { ProgressSpinner } from "primereact/progressspinner";
import useProjectContext from "../hooks/useProjectContext";
import { ANALYTICS_CONTAINER, ANALYTICS_TABLE_NAME } from "../utils/const";
import { LazyState, TableColumn } from "../utils/types";
import { errorToaster, renderSeverity, userTableDetails } from "../utils/utils";
import LazyTable from "./LazyTable";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  tableName: string
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, tableName }) => {
  const {projectId, userSettings} = useProjectContext();
  const [dateFrom, setDateFrom] = React.useState<Date>(getFirstDayOfHalfYear());
  const [dateTo, setDateTo] = React.useState<Date>(getLastDayOfCurrentMonthDate());

  const tableDetails = userTableDetails(userSettings, tableName);

  const [lazyState, setLazyState] = useState<LazyState>({
    first: 0,
    rows: tableDetails?.rows || 10,
    page: 1,
    sortField: "id.date",
    sortOrder: 1,
    multiSortMeta: [],
    filters: {},
    andFilters: [],
    internalFilters: [],
  });

  const [getAnalyticsByObject, {data: analytics, loading}] = useGetAnalyticsByObjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const [getAnalyticsByProject, {data, loading: loading2 }] = useGetAnalyticsByProjectIdPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  const getFilteredData = tableName === ANALYTICS_TABLE_NAME ? getAnalyticsByProject : getAnalyticsByObject
  const totalAnalytics =
    analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.pageInfo?.total ??
    data?.allAnalyticsResultsByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;
  const isLoading = loading || loading2;
  const analyticsData = useMemo(
    () =>
      (analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
        data?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
        []) as AnalyticsResultDto[],
    [analytics, data]
  );

  useEffect(() => {
    if (!isLoading && userSettings) {
      fetchData(tableDetails?.rows, null, null, null, true);
    }
  }, [userSettings, dateTo, dateFrom]);

  const fetchData = (
    take: number | null = null,
    skip: number | null = null,
    sort: SortInput[] | null = null,
    filters: AnyFilterExpressionInput[] | null = null,
    cacheAndNetwork: boolean = false,
  ) => {
    getFilteredData({
      variables: {
        input: {
          id: object?.id ?? projectId,
          range: {
            start: dateFrom,
            startInclusive: true,
            end: dateTo,
            endInclusive: true,
          },
          take: take ?? lazyState.rows,
          skip: skip ?? lazyState.first,
          sort: sort ?? [{
            property: lazyState.sortField,
            direction: lazyState.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
          }],
          andFilters: filters ?? lazyState.andFilters,
        },
      },
      fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
    });
  };
  
  const renderObjectLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/mdm/${projectId}/objects/${rowData.mdmObject?.id}`}>{rowData.mdmObject?.title}</Link>
  );

  const renderDeviceLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`}>{rowData.mdmDevice?.identifier}</Link>
  );

  const commonColumns: TableColumn<AnalyticsResultDto>[] = [
    {
      field: "id.date",
      header: "Date",
      sortable: true,
      body: rowData => parseToLocalZoneDateTime(rowData.id?.date).format(DEFAULT_DATE_FORMATTER),
      columnKey: "date",
    },
    {
      field: "severity",
      header: "Severity",
      body: renderSeverity,
      sortable: true,
      columnKey: "severity",
    },
    {
      field: "id.analyticsType",
      header: "Analytic Type",
      sortable: true,
      columnKey: "analyticType",
    },
    { field: "description", header: "Description", sortable: true, columnKey: "description" },
    {
      field: "deviceType",
      header: "Device Type",
      sortable: true,
      columnKey: "deviceType",
    },
    {
      field: "mdmDevice.identifier",
      header: "Device",
      body: renderDeviceLink,
      hideInFile: true,
      columnKey: "device",
    },
  ];

  const columns: TableColumn<AnalyticsResultDto>[] = !object
    ? [
        ...commonColumns,
        {
          field: "mdmObject.id",
          header: "Object",
          body: renderObjectLink,
          hideInFile: true,
          columnKey: "object",
        },
      ]
    : [...commonColumns];

  return (
    <div className="mt-4">
      <CalendarHeatmapWrapper 
        tableName={tableName}
        queryId={object?.id ?? projectId!}
        dateFrom={dateFrom}
        dateTo={dateTo}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        lazyState={lazyState}
      />
      <LazyTable
        columns={columns}
        data={analyticsData}
        tableName={tableName}
        containerName={ANALYTICS_CONTAINER}
        loading={isLoading}
        onLazyEvent={setLazyState}
        lazyState={lazyState}
        totalItems={totalAnalytics}
        fetchData={fetchData}
      />
    </div>
  );
}

export default ObjectAnalytics;

type CalendarHeatmapWrapperProps = {
  tableName: string;
  queryId: string;
  dateFrom: Date;
  dateTo: Date;
  setDateFrom: React.Dispatch<React.SetStateAction<Date>>;
  setDateTo: React.Dispatch<React.SetStateAction<Date>>;
  lazyState: LazyState;
}

const CalendarHeatmapWrapper: React.FC<CalendarHeatmapWrapperProps> = ({
  tableName,
  queryId,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  lazyState,
}) => {
  const [getAnalyticsByObject, { data: analytics, loading }] =
    useGetAnalyticsByObjectIdPagedLazyQuery({
      onError: err => errorToaster(err),
    });

  const [getAnalyticsByProject, { data, loading: loading2 }] =
    useGetAnalyticsByProjectIdPagedLazyQuery({
      onError: err => errorToaster(err),
    });

  const isLoading = loading || loading2;

  const getFilteredData =
    tableName === ANALYTICS_TABLE_NAME ? getAnalyticsByProject : getAnalyticsByObject;

  const analyticsData = useMemo(
    () =>
      (analytics?.allAnalyticsResultsByObjectIdAndDateRangePaged.items ??
        data?.allAnalyticsResultsByProjectIdAndDateRangePaged.items ??
        []) as AnalyticsResultDto[],
    [analytics, data]
  );

  useEffect(() => {
    getFilteredData({
      variables: {
        input: {
          id: queryId,
          skip: 0,
          take: 0,
          range: {
            start: dateFrom,
            startInclusive: true,
            end: dateTo,
            endInclusive: true,
          },
          sort: [
            {
              property: lazyState.sortField,
              direction: lazyState.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
            },
          ],
          andFilters: lazyState.andFilters,
        },
      },
      fetchPolicy: "cache-and-network",
    });
  }, [dateFrom, dateTo, lazyState.andFilters]);

  if (isLoading) return <ProgressSpinner/>

  const formatHeatData = analyticsData.reduce(
    (prev, curr) => {
      const date = convert(
        parseToLocalZoneDateTime(curr.id?.date).truncatedTo(ChronoUnit.DAYS)
      ).toDate();
      const heatDay = prev.find(day => day.date.getTime() === date.getTime());
      if (heatDay) {
        heatDay.value += 1;
      } else {
        prev.push({ date, value: 1 });
      }
      return prev;
    },
    [] as { date: Date; value: number }[]
  );

  return (
    <CalendarHeatmap
      startDate={dateFrom}
      endDate={dateTo}
      dataset={formatHeatData}
      setStartDate={setDateFrom}
      setEndDate={setDateTo}
      highValueColor="ed0000"
      lowValueColor="ffffff"
      minValue={0}
      maxValue={5}
      staticMinMaxColors
      staticMinMaxTitles={false}
      staticMinMaxValues
      isCustomDataset
      showDateSelection
    />
  );
};